import { useState, useMemo, useEffect, useRef } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  Button
} from "@mui/material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import MapIcon from '@mui/icons-material/Map';
import ClearIcon from '@mui/icons-material/Clear';
import { State, City } from "country-state-city";
import { toast } from "react-toastify";
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { KeyUri } from "../key";

const GOOGLE_API_KEY = "AIzaSyC2cZaJwSU_q3k1Aw2-LHnIaOI7amflxOE";

export function LocationSection({ formData, onChange, errors }) {
  const [selectedState, setSelectedState] = useState(formData.state || "");
  const [selectedCity, setSelectedCity] = useState(formData.city || "");
  const [addressInput, setAddressInput] = useState(formData.address || "");
  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  const STATES = useMemo(() => State.getStatesOfCountry("IN"), []);
  const cityList = useMemo(
    () => (selectedState ? City.getCitiesOfState("IN", selectedState) : []),
    [selectedState]
  );

  // Helper function to extract address components
  const extractAddressComponents = (addressComponents) => {
    const getComponent = (types) =>
      addressComponents.find(c => types.some(t => c.types.includes(t)))?.long_name || "";

    const city =
      getComponent(["locality"]) ||
      getComponent(["postal_town"]) ||
      getComponent(["sublocality_level_1"]) ||
      getComponent(["administrative_area_level_2"]) ||
      "";

    const stateName = getComponent(["administrative_area_level_1"]) || "";
    const pincode = getComponent(["postal_code"]) || "";
    const country = getComponent(["country"]) || "";

    return { city, stateName, pincode, country };
  };

  // Helper function to update all location fields
  const updateLocationFields = (data) => {
    const { address, city, stateName, pincode, country, lat, lng, placeId } = data;

    if (address) {
      onChange("address", address);
      setAddressInput(address);
    }
    if (city) onChange("city", city);
    if (stateName) onChange("state", stateName);
    if (pincode) onChange("pincode", pincode);
    if (country) onChange("country", country);
    if (lat !== undefined) onChange("latitude", lat);
    if (lng !== undefined) onChange("longitude", lng);
    if (placeId) onChange("placeId", placeId);

    if (lat !== undefined && lng !== undefined) {
      const shareUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      onChange("mapShareLink", shareUrl);
    }

    // Map state name to ISO code for Select dropdown
    if (stateName) {
      const matchedState = STATES.find(
        s => s.name.toLowerCase() === stateName.toLowerCase()
      );
      if (matchedState) {
        setSelectedState(matchedState.isoCode);
      }
    }

    if (city) setSelectedCity(city);
  };

 
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
      );
      const data = await res.json();

      if (data.status !== 'OK' || !data.results.length) {
        toast.error('Could not get address from coordinates');
        return null;
      }

      const place = data.results[0];
      const { city, stateName, pincode, country } = extractAddressComponents(place.address_components || []);

      return {
        address: place.formatted_address || "",
        city,
        stateName,
        pincode,
        country,
        lat,
        lng
      };
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch address');
      return null;
    }
  };

 
  const parseGoogleMapsLink = (link) => {
    if (!link) return null;

   

    let lat, lng;

   
    const queryMatch = link.match(/[?&]q(?:uery)?=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (queryMatch) {
      lat = parseFloat(queryMatch[1]);
      lng = parseFloat(queryMatch[2]);
    }

    
    if (!lat || !lng) {
      const atMatch = link.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (atMatch) {
        lat = parseFloat(atMatch[1]);
        lng = parseFloat(atMatch[2]);
      }
    }

    
    if (!lat || !lng) {
      const placeMatch = link.match(/place\/[^/]+\/(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (placeMatch) {
        lat = parseFloat(placeMatch[1]);
        lng = parseFloat(placeMatch[2]);
      }
    }

    
    if (!lat || !lng) {
      const coordMatch = link.match(/(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/);
      if (coordMatch) {
        lat = parseFloat(coordMatch[1]);
        lng = parseFloat(coordMatch[2]);
      }
    }

    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }

    return null;
  };

  
  const searchPlaceByName = async (placeName) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(placeName)}&components=country:IN&key=${GOOGLE_API_KEY}`
      );
      const data = await res.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const result = data.results[0];
        const { city, stateName, pincode, country } = extractAddressComponents(result.address_components || []);
        const lat = result.geometry.location.lat;
        const lng = result.geometry.location.lng;

        return {
          address: result.formatted_address || "",
          city,
          stateName,
          pincode,
          country,
          lat,
          lng
        };
      }
    } catch (err) {
      console.error("Error searching for place:", err);
    }
    return null;
  };


  const expandShortLinkViaBackend = async (shortUrl) => {
    try {
      const response = await fetch(`${KeyUri.BACKEND_URI}/expand-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: shortUrl })
      });

      if (!response.ok) {
        throw new Error('Failed to expand URL');
      }

      const data = await response.json();
      return data.expandedUrl || data.url || null;
    } catch (err) {
      console.error("Error expanding URL via backend:", err);
      return null;
    }
  };

  const handleShareLinkChange = async (e) => {
    const link = e.target.value.trim();
    onChange("mapShareLink", link);

    if (!link) return;

   
    const isShortLink = link.includes("maps.app.goo.gl") || link.includes("goo.gl/maps");

    if (isShortLink) {
      toast.info("Processing link... Please wait.");

      try {
        
        const expandedUrl = await expandShortLinkViaBackend(link);

        if (expandedUrl) {
         
          const coords = parseGoogleMapsLink(expandedUrl);
          if (coords) {
            const locationData = await reverseGeocode(coords.lat, coords.lng);
            if (locationData) {
              updateLocationFields(locationData);
              toast.success("Location auto-filled from share link!");
              return;
            }
          }

         
          const placeNameMatch = expandedUrl.match(/place\/([^\/\@]+)/);
          if (placeNameMatch) {
            const placeName = decodeURIComponent(placeNameMatch[1].replace(/\+/g, ' '));
            const locationData = await searchPlaceByName(placeName);
            if (locationData) {
              updateLocationFields(locationData);
              toast.success("Location found and auto-filled!");
              return;
            }
          }
        }

        
        toast.warning("Could not process this link automatically. Please try the 'Use current location' or 'Open Google Maps' buttons above.", { autoClose: 6000 });
      } catch (err) {
        console.error("Error processing short link:", err);
        toast.error("Failed to process link. Please use other options above.");
      }
      return;
    }

    
    const coords = parseGoogleMapsLink(link);
    if (coords) {
      toast.info("Extracting location from link...");
      const locationData = await reverseGeocode(coords.lat, coords.lng);
      if (locationData) {
        updateLocationFields(locationData);
        toast.success("Location auto-filled from share link!");
        return;
      }
    }

   
    const placeNameMatch = link.match(/place\/([^\/\@]+)/);
    if (placeNameMatch) {
      const placeName = decodeURIComponent(placeNameMatch[1].replace(/\+/g, ' '));
      toast.info(`Searching for "${placeName}"...`);

      const locationData = await searchPlaceByName(placeName);
      if (locationData) {
        updateLocationFields(locationData);
        toast.success("Location found and auto-filled!");
        return;
      }
    }

    
    const searchMatch = link.match(/search\/([^\/\@]+)/) || link.match(/[?&]q=([^&]+)/);
    if (searchMatch) {
      const searchQuery = decodeURIComponent(searchMatch[1].replace(/\+/g, ' '));
      toast.info(`Searching for "${searchQuery}"...`);

      const locationData = await searchPlaceByName(searchQuery);
      if (locationData) {
        updateLocationFields(locationData);
        toast.success("Location found and auto-filled!");
        return;
      }
    }

    
    toast.warning("Could not extract location. Please use 'Use current location' or 'Open Google Maps' buttons.", { autoClose: 5000 });
  };

 
  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }
    if (!addressRef.current) return;

    
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    const autocomplete = new window.google.maps.places.Autocomplete(addressRef.current, {
      types: ["geocode"],
      componentRestrictions: { country: "IN" },
      fields: ["address_components", "geometry", "formatted_address", "place_id"],
    });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) {
        toast.error("No details available for the selected place");
        return;
      }

      const { city, stateName, pincode, country } = extractAddressComponents(place.address_components || []);
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      updateLocationFields({
        address: place.formatted_address || "",
        city,
        stateName,
        pincode,
        country,
        lat,
        lng,
        placeId: place.place_id
      });

      toast.success("Location auto-filled from selection");
    });

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [STATES, onChange]);

  
  const handleAddressInputChange = (e) => {
    const value = e.target.value;
    setAddressInput(value);
    onChange("address", value);
  };

 
  const handleClearAddress = () => {
    setAddressInput("");
    onChange("address", "");
    
    onChange("latitude", "");
    onChange("longitude", "");
    onChange("pincode", "");
    onChange("mapShareLink", "");
    setSelectedState("");
    setSelectedCity("");
    onChange("state", "");
    onChange("city", "");
  
    if (addressRef.current) {
      addressRef.current.focus();
    }
  };

  const handleStateChange = (e) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedCity("");
    onChange("state", state);
    onChange("city", "");
  };

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    onChange("city", city);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported in this browser');
      return;
    }

    toast.info("Getting your location...");

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        const locationData = await reverseGeocode(lat, lng);
        if (locationData) {
          updateLocationFields(locationData);
          toast.success('Location filled from your current position');
        }
      },
      (err) => {
        toast.error(err.message || 'Unable to get current location');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Open Google Maps directly
  const handleOpenGoogleMaps = () => {
    // Open Google Maps centered on India or current location if available
    let mapsUrl = "https://www.google.com/maps";

    if (formData.latitude && formData.longitude) {
      // If we already have coordinates, open at that location
      mapsUrl = `https://www.google.com/maps/@${formData.latitude},${formData.longitude},15z`;
    } else {
      // Open Google Maps centered on India
      mapsUrl = "https://www.google.com/maps/@20.5937,78.9629,5z";
    }

    window.open(mapsUrl, "_blank");
    toast.info("Find your location → Right-click → 'What's here?' → Copy the coordinates (e.g., 13.0827, 80.2707) and paste in Latitude/Longitude fields", { autoClose: 8000 });
  };

  return (
    <Box sx={{ my: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <LocationOnIcon sx={{ color: "primary.main" }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Location Details
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Tip: Use your current location, select from map, or paste a Google Maps link.
        </Typography>

        <Tooltip title="Detect my current location">
          <Button
            variant="contained"
            size="small"
            startIcon={<MyLocationIcon />}
            onClick={handleUseCurrentLocation}
          >
            Use current location
          </Button>
        </Tooltip>

        <Tooltip title="Open Google Maps to find location">
          <Button
            variant="outlined"
            size="small"
            startIcon={<MapIcon />}
            onClick={handleOpenGoogleMaps}
          >
            Open Google Maps
          </Button>
        </Tooltip>
      </Box>

      <TextField
        fullWidth
        label="Full Address *"
        id="address"
        inputRef={addressRef}
        value={addressInput}
        onChange={handleAddressInputChange}
        placeholder="Start typing address or place name"
        error={Boolean(errors?.address)}
        helperText={errors?.address || "Type to search, or use buttons above to auto-fill. Click X to clear."}
        sx={{ mb: 3 }}
        InputProps={{
          endAdornment: addressInput ? (
            <InputAdornment position="end">
              <Tooltip title="Clear address and all location fields">
                <IconButton
                  onClick={handleClearAddress}
                  size="small"
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={Boolean(errors?.state)}>
            <InputLabel id="state-label">State *</InputLabel>
            <Select
              labelId="state-label"
              value={selectedState}
              label="State *"
              onChange={handleStateChange}
            >
              {STATES.map((state) => (
                <MenuItem key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={Boolean(errors?.city)}>
            <InputLabel id="city-label">City *</InputLabel>
            <Select
              labelId="city-label"
              value={selectedCity}
              label="City *"
              onChange={handleCityChange}
              disabled={!selectedState}
            >
              {cityList.map((city) => (
                <MenuItem key={city.name} value={city.name}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Pincode"
            value={formData.pincode || ""}
            onChange={(e) => onChange("pincode", e.target.value)}
            error={Boolean(errors?.pincode)}
            helperText={errors?.pincode}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Latitude"
            value={formData.latitude || ""}
            onChange={(e) => onChange("latitude", e.target.value)}
            placeholder="e.g., 13.0827"
            helperText="Paste latitude here"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Longitude"
            value={formData.longitude || ""}
            onChange={(e) => onChange("longitude", e.target.value)}
            placeholder="e.g., 80.2707"
            helperText={
              <Button
                size="small"
                variant="text"
                disabled={!formData.latitude || !formData.longitude}
                onClick={async () => {
                  const lat = parseFloat(formData.latitude);
                  const lng = parseFloat(formData.longitude);
                  if (!isNaN(lat) && !isNaN(lng)) {
                    toast.info("Fetching address from coordinates...");
                    const locationData = await reverseGeocode(lat, lng);
                    if (locationData) {
                      updateLocationFields(locationData);
                      toast.success("Address filled from coordinates!");
                    }
                  } else {
                    toast.error("Please enter valid latitude and longitude");
                  }
                }}
                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
              >
                {formData.latitude && formData.longitude ? "Click to auto-fill address from coordinates" : "Enter lat/lng to auto-fill"}
              </Button>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Google Maps Share Link"
            value={formData.mapShareLink || ""}
            onChange={handleShareLinkChange}
            placeholder="Paste the full URL from Google Maps address bar"
            helperText="Paste the full URL from your browser's address bar (not short links like maps.app.goo.gl)"
            InputProps={{
              endAdornment: formData.mapShareLink ? (
                <InputAdornment position="end">
                  <Tooltip title="Open in Google Maps">
                    <IconButton
                      onClick={() => window.open(formData.mapShareLink, "_blank")}
                      size="small"
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
      </Grid>
{/* How To Reach */}
<Box sx={{ mt: 4 }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      How To Reach (Optional)
    </Typography>
  </Box>

  <Grid container spacing={2}>

    {/* Overview */}
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Overview"
        value={formData.how_to_reach?.overview || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          overview: e.target.value
        })}
        placeholder="e.g., The property is best reached via Haridwar or Rishikesh..."
      />
    </Grid>

    {/* By Air */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        By Air
      </Typography>
    </Grid>
    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        label="Airport Name"
        value={formData.how_to_reach?.by_air?.airport_name || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_air: { ...formData.how_to_reach?.by_air, airport_name: e.target.value }
        })}
        placeholder="Jolly Grant Airport"
      />
    </Grid>
    <Grid item xs={12} md={2}>
      <TextField
        fullWidth
        label="Code"
        value={formData.how_to_reach?.by_air?.airport_code || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_air: { ...formData.how_to_reach?.by_air, airport_code: e.target.value }
        })}
        placeholder="DED"
      />
    </Grid>
    <Grid item xs={12} md={3}>
      <TextField
        fullWidth
        label="Distance"
        value={formData.how_to_reach?.by_air?.distance_label || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_air: { ...formData.how_to_reach?.by_air, distance_label: e.target.value }
        })}
        placeholder="35 km / ~1 hr drive"
      />
    </Grid>
   <Grid item xs={12} md={3}>
  <TextField
    fullWidth
    type="number"
    label="Distance (km)"
    value={formData.how_to_reach?.by_air?.distance_km || ""}
    onChange={(e) => {
      const value = e.target.value;

      onChange("how_to_reach", {
        ...formData.how_to_reach,
        by_air: {
          ...formData.how_to_reach?.by_air,
          distance_km: value === "" ? "" : Number(value)
        }
      });
    }}
    inputProps={{ min: 0 }}
  />
</Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Instructions"
        value={formData.how_to_reach?.by_air?.instructions || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_air: { ...formData.how_to_reach?.by_air, instructions: e.target.value }
        })}
        placeholder="Take a prepaid taxi from the airport exit..."
      />
    </Grid>

    {/* By Train */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        By Train
      </Typography>
    </Grid>
    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        label="Station Name"
        value={formData.how_to_reach?.by_train?.station_name || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_train: { ...formData.how_to_reach?.by_train, station_name: e.target.value }
        })}
        placeholder="Haridwar Railway Station"
      />
    </Grid>
    <Grid item xs={12} md={2}>
      <TextField
        fullWidth
        label="Code"
        value={formData.how_to_reach?.by_train?.station_code || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_train: { ...formData.how_to_reach?.by_train, station_code: e.target.value }
        })}
        placeholder="HW"
      />
    </Grid>
    <Grid item xs={12} md={3}>
      <TextField
        fullWidth
        label="Distance"
        value={formData.how_to_reach?.by_train?.distance_label || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_train: { ...formData.how_to_reach?.by_train, distance_label: e.target.value }
        })}
        placeholder="24 km / ~45 min drive"
      />
    </Grid>
   <Grid item xs={12} md={3}>
  <TextField
    fullWidth
    type="number"
    label="Distance (km)"
    value={formData.how_to_reach?.by_train?.distance_km || ""}
    onChange={(e) => {
      const value = e.target.value;

      onChange("how_to_reach", {
        ...formData.how_to_reach,
        by_train: {
          ...formData.how_to_reach?.by_train,
          distance_km: value === "" ? "" : Number(value)
        }
      });
    }}
    inputProps={{ min: 0 }}
  />
</Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Instructions"
        value={formData.how_to_reach?.by_train?.instructions || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_train: { ...formData.how_to_reach?.by_train, instructions: e.target.value }
        })}
        placeholder="Auto-rickshaws and taxis available outside the station..."
      />
    </Grid>

    {/* By Road */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
       By Road
      </Typography>
    </Grid>
    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        label="Nearest Bus Stand"
        value={formData.how_to_reach?.by_road?.bus_stand || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_road: { ...formData.how_to_reach?.by_road, bus_stand: e.target.value }
        })}
        placeholder="ISBT Rishikesh"
      />
    </Grid>
    <Grid item xs={12} md={4}>
      <TextField
        fullWidth
        label="Distance Label"
        value={formData.how_to_reach?.by_road?.distance_label || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_road: { ...formData.how_to_reach?.by_road, distance_label: e.target.value }
        })}
        placeholder="250 km from Delhi (~5 hr drive)"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Road Instructions"
        value={formData.how_to_reach?.by_road?.instructions || ""}
        onChange={(e) => onChange("how_to_reach", {
          ...formData.how_to_reach,
          by_road: { ...formData.how_to_reach?.by_road, instructions: e.target.value }
        })}
        placeholder="Take NH58 from Delhi toward Haridwar..."
      />
    </Grid>

    {/* Local Transfer */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Local Transfer
      </Typography>
    </Grid>
    <Grid item xs={12} md={4}>
      <FormControl fullWidth>
        <InputLabel>Transfer Available?</InputLabel>
        <Select
          value={formData.how_to_reach?.local_transfer?.available ? "true" : "false"}
          label="Transfer Available?"
          onChange={(e) => onChange("how_to_reach", {
            ...formData.how_to_reach,
            local_transfer: {
              ...formData.how_to_reach?.local_transfer,
              available: e.target.value === "true"
            }
          })}
        >
          <MenuItem value="false">No</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    {formData.how_to_reach?.local_transfer?.available && (
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label="Transfer Details"
          value={formData.how_to_reach?.local_transfer?.details || ""}
          onChange={(e) => onChange("how_to_reach", {
            ...formData.how_to_reach,
            local_transfer: {
              ...formData.how_to_reach?.local_transfer,
              details: e.target.value
            }
          })}
          placeholder="Pick-up available from Haridwar station at ₹500 per person"
        />
      </Grid>
    )}

  </Grid>
</Box>
    </Box>
  );
}
